import axios from 'axios';
import { createAsyncThunk, createSlice, isFulfilled, isPending, isRejected } from '@reduxjs/toolkit';

import { cleanEntity } from 'app/shared/util/entity-utils';
import { serializeAxiosError } from 'app/shared/reducers/reducer.utils';
import { IFileMetadata, IFileData } from 'app/shared/model/file.model';
import { convertFileToBase64 } from 'app/shared/util/file-utils';

export interface FileUploadDTO {
  fileName: string;
  fileType: string;
  contentType: string;
  fileSize: number;
  data: string;
  className?: string;
  entityId?: number;
  orderIndex?: number;
}

export interface QuotationFileDTO {
  fileType: string;
  fileName: string;
  file: string; // Base64 encoded
  order: number;
}

export interface QuotationDocumentsDTO {
  transactionRefNo: string;
  files: QuotationFileDTO[];
}

export interface FileState {
  loading: boolean;
  errorMessage: string | null;
  entities: ReadonlyArray<IFileMetadata>;
  entity: IFileMetadata | null;
  updating: boolean;
  updateSuccess: boolean;
}

const initialState: FileState = {
  loading: false,
  errorMessage: null,
  entities: [],
  entity: null,
  updating: false,
  updateSuccess: false,
};

const apiUrl = 'api/files';

// Actions - based on available controller endpoints

export const getFileMetadata = createAsyncThunk(
  'file/fetch_metadata',
  async (id: string | number) => {
    const requestUrl = `${apiUrl}/metadata/${id}`;
    return axios.get<IFileMetadata>(requestUrl);
  },
  { serializeError: serializeAxiosError }
);

export const getFileData = createAsyncThunk(
  'file/fetch_data',
  async (id: string | number) => {
    const requestUrl = `${apiUrl}/data/${id}`;
    return axios.get<IFileData>(requestUrl);
  },
  { serializeError: serializeAxiosError }
);

export const getFileContent = createAsyncThunk(
  'file/fetch_content',
  async (id: string | number) => {
    const requestUrl = `${apiUrl}/${id}`;
    return axios.get(requestUrl, {
      responseType: 'blob',
      // This is necessary to ensure the response headers are properly accessible
      responseEncoding: 'binary',
    });
  },
  { serializeError: serializeAxiosError }
);

export const getFilesForEntity = createAsyncThunk(
  'file/fetch_entity_files',
  async ({ className, entityId }: { className: string; entityId: string | number }) => {
    const requestUrl = `api/entities/${encodeURIComponent(className)}/${entityId}/files`;
    return axios.get<IFileMetadata[]>(requestUrl);
  },
  { serializeError: serializeAxiosError }
);

export const getQuotationDocuments = createAsyncThunk(
  'file/fetch_quotation_documents',
  async (id: string | number) => {
    const requestUrl = `api/quotations/${id}/documents`;
    return axios.get<IFileMetadata[]>(requestUrl);
  },
  { serializeError: serializeAxiosError }
);

export const uploadFile = createAsyncThunk(
  'file/upload_file',
  async (file: FileUploadDTO) => {
    return axios.post<IFileMetadata>(apiUrl, cleanEntity(file));
  },
  { serializeError: serializeAxiosError }
);

export const uploadQuotationDocuments = createAsyncThunk(
  'file/upload_quotation_documents',
  async ({ id, documents }: { id: string | number; documents: QuotationDocumentsDTO }) => {
    const requestUrl = `api/quotations/${id}/documents`;
    return axios.post(requestUrl, cleanEntity(documents));
  },
  { serializeError: serializeAxiosError }
);

export const uploadAmountTransferredDocument = createAsyncThunk(
  'file/upload_amount_transferred_document',
  async ({ id, documents }: { id: string | number; documents: QuotationDocumentsDTO }) => {
    const requestUrl = `api/quotations/${id}/amountTransferredDocument`;
    return axios.post(requestUrl, cleanEntity(documents));
  },
  { serializeError: serializeAxiosError }
);

export const deleteFile = createAsyncThunk(
  'file/delete_entity',
  async (id: string | number) => {
    const requestUrl = `${apiUrl}/${id}`;
    return axios.delete(requestUrl);
  },
  { serializeError: serializeAxiosError }
);

export const deleteAllFilesForEntity = createAsyncThunk(
  'file/delete_all_entity_files',
  async ({ className, entityId }: { className: string; entityId: string | number }) => {
    const requestUrl = `api/entities/${encodeURIComponent(className)}/${entityId}/files`;
    return axios.delete(requestUrl);
  },
  { serializeError: serializeAxiosError }
);

export const deleteAllFilesForQuotation = createAsyncThunk(
  'file/delete_all_quotation_files',
  async (quotationId: string | number) => {
    const requestUrl = `api/quotations/${quotationId}/documents`;
    return axios.delete(requestUrl);
  },
  { serializeError: serializeAxiosError }
);

// Slice

export const FileSlice = createSlice({
  name: 'file',
  initialState,
  reducers: {
    reset() {
      return initialState;
    },
  },
  extraReducers(builder) {
    builder
      .addCase(getFileMetadata.fulfilled, (state, action) => {
        state.loading = false;
        state.entity = action.payload.data;
      })
      .addCase(deleteFile.fulfilled, state => {
        state.updating = false;
        state.updateSuccess = true;
        state.entity = null;
      })
      .addCase(deleteAllFilesForQuotation.fulfilled, state => {
        state.updating = false;
        state.updateSuccess = true;
        state.entities = [];
      })
      .addCase(deleteAllFilesForEntity.fulfilled, state => {
        state.updating = false;
        state.updateSuccess = true;
        state.entities = [];
      })
      .addCase(getFileContent.fulfilled, state => {
        state.loading = false;
      })
      .addMatcher(isFulfilled(getFilesForEntity, getQuotationDocuments), (state, action) => {
        return {
          ...state,
          loading: false,
          entities: action.payload.data,
        };
      })
      .addMatcher(isFulfilled(uploadFile), (state, action) => {
        state.updating = false;
        state.loading = false;
        state.updateSuccess = true;
        state.entity = action.payload.data;
      })
      .addMatcher(isFulfilled(uploadQuotationDocuments, uploadAmountTransferredDocument), state => {
        state.updating = false;
        state.loading = false;
        state.updateSuccess = true;
      })
      .addMatcher(isPending(getFileMetadata, getFileData, getFileContent, getFilesForEntity, getQuotationDocuments), state => {
        state.errorMessage = null;
        state.updateSuccess = false;
        state.loading = true;
      })
      .addMatcher(
        isPending(
          uploadFile,
          uploadQuotationDocuments,
          uploadAmountTransferredDocument,
          deleteFile,
          deleteAllFilesForEntity,
          deleteAllFilesForQuotation
        ),
        state => {
          state.errorMessage = null;
          state.updateSuccess = false;
          state.updating = true;
        }
      )
      .addMatcher(
        isRejected(
          getFileMetadata,
          getFileData,
          getFileContent,
          getFilesForEntity,
          getQuotationDocuments,
          uploadFile,
          uploadQuotationDocuments,
          deleteFile,
          deleteAllFilesForEntity,
          deleteAllFilesForQuotation
        ),
        (state, action) => {
          state.loading = false;
          state.updating = false;
          state.errorMessage = action.error.message;
        }
      );
  },
});

export const { reset } = FileSlice.actions;

// Reducer
export default FileSlice.reducer;
